'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { useState, useContext, useRef, useId } from 'react';
import {
    DatePicker as AriaDatePicker,
    type DatePickerProps as AriaDatePickerProps,
    DateInput as AriaDateInput,
    DateSegment as AriaDateSegment
} from 'react-aria-components/DatePicker';
import { Group } from 'react-aria-components/Group';
import { Button as AriaButton } from 'react-aria-components/Button';
import { Dialog } from 'react-aria-components/Dialog';
import {
    Calendar,
    CalendarGrid,
    CalendarGridHeader,
    CalendarGridBody,
    CalendarCell,
    CalendarHeaderCell
} from 'react-aria-components/Calendar';
import { Heading } from 'react-aria-components/Heading';
import { CalendarDate, type DateValue } from '@internationalized/date';
import { cn } from '../../utils/cn';
import { IconCalendar } from '../icons/IconCalendar.ts';
import { IconChevronLeft } from '../icons/IconChevronLeft.ts';
import { IconChevronRight } from '../icons/IconChevronRight.ts';
import { IconCautionCircleStroke } from '../icons/IconCautionCircleStroke.ts';
import { IconCheckCircleStroke } from '../icons/IconCheckCircleStroke.ts';
import { TextBaseInput, type TextBaseInputProps } from '../TextInput/TextBaseInput.tsx';
import Popover from '../Popover';
import { useScrollOverride } from '../../hooks/useScrollOverride.ts';
import { ModalPresenceContext } from '../Modal/Modal.tsx';

const inputGroupVariants = cva(
    `
        flex h-lr-size-600 w-full items-center rounded-lr-main border-lr-main border-lr-border-active
        text-lr-body-standard-light text-lr-text-main caret-lr-border-focus outline-0 outline-offset-lr-focus
        outline-lr-border-focus transition-colors
        data-disabled:cursor-not-allowed data-disabled:border-lr-border-disabled data-disabled:bg-lr-bg-disabled
        data-disabled:text-lr-text-subtle
        data-focus-within:outline-lr-focus
    `,
    {
        variants: {
            state: {
                default: 'border-lr-border-medium',
                success: 'border-lr-border-success bg-lr-bg-success-subtle text-lr-text-success',
                error: 'border-lr-border-error bg-lr-bg-error-subtle text-lr-text-error'
            }
        },
        defaultVariants: {
            state: 'default'
        }
    }
);

type BaseProps = Omit<TextBaseInputProps<false>, 'children' | 'notTextFieldBasedInput'>;

export interface DatePickerProps<T extends DateValue>
    extends Omit<AriaDatePickerProps<T>, 'className'>,
        Pick<BaseProps, 'label' | 'optionalLabel' | 'hint' | 'indications' | 'isRequired' | 'showRequiredIndicator'>,
        VariantProps<typeof inputGroupVariants> {
    className?: string;
    minDate?: Date;
    maxDate?: Date;
    initialValue?: Date;
}

/**
 * Default placeholder date used by the segmented input when no value is set.
 * It is intentionally in a 31-day month (January) so the day segment accepts up
 * to 31 even before a month is picked. It is never displayed and does not affect
 * the month shown when the calendar popover opens (that stays on today).
 */
const DEFAULT_PLACEHOLDER_VALUE = new CalendarDate(2000, 1, 1);

const navButtonClassName = cn(
    'flex size-lr-size-400 cursor-pointer items-center justify-center',
    'rounded-lr-full text-lr-icon-main outline-none',
    'data-disabled:cursor-not-allowed data-disabled:opacity-50',
    'data-focus-visible:outline-lr-focus data-focus-visible:outline-offset-lr-focus',
    'data-hovered:bg-lr-bg-hover'
);

const StateIcon = ({ state }: Pick<DatePickerProps<DateValue>, 'state'>) => {
    if (!state) return null;

    return (
        <div
            className={cn(
                'mr-lr-spacing-125 flex items-center justify-center',
                state === 'success' ? 'text-lr-icon-success' : 'text-lr-icon-error'
            )}
        >
            {state === 'success' ? <IconCheckCircleStroke size="sm" /> : <IconCautionCircleStroke size="sm" />}
        </div>
    );
};

function DatePicker<T extends DateValue>({
    state = 'default',
    className,
    indications,
    isRequired,
    label,
    optionalLabel,
    hint,
    showRequiredIndicator,
    placeholderValue,
    ...rest
}: DatePickerProps<T>) {
    const hasError = !!indications?.some((i) => i.type === 'error');
    const [isOpen, setIsOpen] = useState(false);
    const isInModal = useContext(ModalPresenceContext);
    const groupRef = useRef<HTMLDivElement>(null);
    const labelId = useId();
    const inputId = useId();

    // Allow page scroll when datepicker popover is open, except inside a modal
    useScrollOverride(isOpen && !isInModal, 'datepicker-scroll-override');

    return (
        <TextBaseInput
            notTextFieldBasedInput
            indications={indications}
            isRequired={isRequired}
            isInvalid={hasError}
            label={label}
            optionalLabel={optionalLabel}
            hint={hint}
            showRequiredIndicator={showRequiredIndicator}
            labelId={labelId}
            labelHtmlFor={inputId}
        >
                <AriaDatePicker
                    className={cn('group relative', className)}
                    isInvalid={hasError}
                    isRequired={isRequired}
                    onOpenChange={setIsOpen}
                    placeholderValue={placeholderValue ?? (DEFAULT_PLACEHOLDER_VALUE as T)}
                    {...rest}
                >
                    <Group aria-labelledby={labelId} ref={groupRef} className={cn(inputGroupVariants({ state }))}>
                        <input
                            id={inputId}
                            tabIndex={-1}
                            aria-hidden="true"
                            className="sr-only"
                            onFocus={() => {
                                groupRef.current
                                    ?.querySelector<HTMLElement>('[data-type]:not([data-type=literal])')
                                    ?.focus();
                            }}
                        />
                        <AriaDateInput className="flex flex-1 items-center pr-lr-spacing-700 pl-lr-spacing-150">
                            {(segment) => (
                                <AriaDateSegment
                                    segment={segment}
                                    className={cn(
                                        `
                                            rounded-lr-main px-lr-spacing-025 text-lr-body-standard-light
                                            focus:bg-lr-bg-active focus:text-lr-bg-active-inverse
                                        `,
                                        'outline-none',
                                        'placeholder-shown:text-lr-text-subtle',
                                        'data-[type=literal]:p-0',
                                        state === 'error' && 'text-lr-text-error',
                                        state === 'success' && 'text-lr-text-success',
                                        state === 'default' && 'text-lr-text-main'
                                    )}
                                />
                            )}
                        </AriaDateInput>

                        <div
                            className={cn(
                                'absolute top-1/2 right-lr-spacing-150 flex -translate-y-1/2 items-center',
                                'gap-lr-spacing-025'
                            )}
                        >
                            {state !== 'default' && <StateIcon state={state} />}

                            <AriaButton
                                className={cn(
                                    'hidden size-lr-size-400 items-center justify-center',
                                    'lr-md:flex!', // Fix conflit with legacy hidden class
                                    'rounded-lr-main text-lr-icon-main outline-none',
                                    'cursor-pointer'
                                )}
                            >
                                <IconCalendar size="sm" />
                            </AriaButton>
                        </div>
                    </Group>

                    <Popover offset={4}>
                        <Dialog className="pt-lr-spacing-200 outline-none">
                            <Calendar>
                                <header
                                    className={cn(`
                                        mb-lr-spacing-100 flex items-center justify-between pr-lr-spacing-100
                                        pl-lr-spacing-200
                                    `)}
                                >
                                    <Heading className="text-lr-body-standard-medium text-lr-text-main capitalize" />
                                    <div className="flex items-center gap-lr-spacing-050">
                                        <AriaButton slot="previous" className={navButtonClassName}>
                                            <IconChevronLeft size="sm" />
                                        </AriaButton>
                                        <AriaButton slot="next" className={navButtonClassName}>
                                            <IconChevronRight size="sm" />
                                        </AriaButton>
                                    </div>
                                </header>
                                <CalendarGrid className="w-full border-separate border-spacing-lr-spacing-025">
                                    <CalendarGridHeader>
                                        {(day) => (
                                            <CalendarHeaderCell
                                                className={`text-lr-body-small-medium text-lr-text-subtle`}
                                            >
                                                {day}
                                            </CalendarHeaderCell>
                                        )}
                                    </CalendarGridHeader>
                                    <CalendarGridBody className="[&_td]:h-lr-size-500 [&_td]:w-lr-size-500">
                                        {(date) => (
                                            <CalendarCell
                                                date={date}
                                                className={cn(
                                                    'flex h-full w-full cursor-pointer items-center',
                                                    'justify-center rounded-lr-full bg-lr-bg-main',
                                                    'text-lr-body-small-medium',
                                                    'outline-none',
                                                    'data-disabled:cursor-not-allowed',
                                                    'data-disabled:text-lr-text-disabled',
                                                    'data-disabled:opacity-50',
                                                    'data-focus-visible:outline-lr-focus',
                                                    'data-focus-visible:outline-offset-lr-focus',
                                                    'data-hovered:bg-lr-bg-hover',
                                                    'data-outside-month:text-lr-text-subtle',
                                                    `
                                                        data-selected:bg-lr-bg-active
                                                        data-selected:text-lr-text-main-inverse
                                                    `
                                                )}
                                            />
                                        )}
                                    </CalendarGridBody>
                                </CalendarGrid>
                            </Calendar>
                        </Dialog>
                    </Popover>
                </AriaDatePicker>
            </TextBaseInput>
    );
}

DatePicker.displayName = 'TwDatePicker';
export { DatePicker };
export default DatePicker;
